import React from 'react'
import {
  TreeViewIcon,
  DatabaseIcon,
  IconProps,
  SparkleIcon,
  BookOpenIcon,
  SignOutIcon,
  UserIcon,
} from '@phosphor-icons/react'
import { Link, useNavigate, useRouterState } from '@tanstack/react-router'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { ComingSoonBadge } from '@/components/ui/coming-soon-badge'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useAuth } from '@/components/auth'
import { useUserProfile } from '@/api/use-user-profile-query'
import { featureFlags } from '@/lib/feature-flags'
import { EmergenceIcon } from './emergence-icon'
import { DashboardCustomizeIcon } from './icons/dashboard-customize-icon'

// Animated wrapper for DatabaseIcon - segments separate vertically on hover
const AnimatedDatabaseIcon = ({
  className,
  weight,
}: {
  className?: string
  weight?: IconProps['weight']
}) => (
  <div
    className={cn(
      className,
      '[&>svg]:transition-transform [&>svg]:duration-300 [&>svg]:ease-in-out group-hover:[&>svg]:scale-y-105',
    )}
  >
    <DatabaseIcon className='size-full' weight={weight} />
  </div>
)

const NAV_ITEMS: MenuItem[] = [
  { title: 'Dashboard', url: '/', Icon: DashboardCustomizeIcon },
  { title: 'Workflows', url: '/workflows', Icon: TreeViewIcon },
  {
    title: 'Studio',
    url: '/studio',
    Icon: SparkleIcon,
    disabled: !featureFlags.studio,
    beta: true,
  },
  {
    title: 'Knowledge',
    url: '/knowledge',
    Icon: BookOpenIcon,
    disabled: !featureFlags.knowledge,
    beta: true,
  },
  { title: 'Data Hub', url: '/data', Icon: AnimatedDatabaseIcon },
]

type SidebarNavIconProps = {
  className?: string
  weight?: IconProps['weight']
}

type MenuItem = {
  title: string
  url: string
  Icon: React.ComponentType<SidebarNavIconProps>
  disabled?: boolean
  beta?: boolean
}

type MenuButtonProps = Readonly<
  MenuItem & {
    isActive: boolean
  }
>

function MenuButton({ title, url, Icon, isActive, disabled, beta }: MenuButtonProps) {
  return (
    <Button
      asChild
      variant='unstyled'
      className={cn(
        'group w-full select-none rounded-lg font-sans text-xs font-medium -outline-offset-4 outline-orange-500 transition-colors',
        disabled ? 'cursor-not-allowed py-2 opacity-60' : 'py-2.5',
        'flex flex-col items-center justify-center gap-1.5 text-neutral-950',
        isActive && 'bg-neutral-200',
        !disabled && !isActive && 'hover:bg-neutral-300',
      )}
      disabled={disabled}
    >
      <Link
        to={url}
        preload={false}
        data-testid={`nav-${title.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <Icon className='size-6 shrink-0 text-neutral-950' weight='regular' />
        <span className='w-full truncate text-center'>{title}</span>
        {disabled ? (
          <ComingSoonBadge className='mt-0.5 px-1.5 py-0.5 text-[9px]' />
        ) : beta ? (
          <span
            className='mt-0.5 inline-flex items-center whitespace-nowrap rounded-full bg-orange-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase leading-none tracking-wide text-orange-700'
            data-testid={`nav-${title.toLowerCase().replace(/\s+/g, '-')}-beta`}
          >
            beta
          </span>
        ) : null}
      </Link>
    </Button>
  )
}

export function AppSidebar() {
  const router = useRouterState()
  const location = router.location.pathname
  const navigate = useNavigate()
  const { logout } = useAuth()
  const { data: userProfile } = useUserProfile({ staleTime: Infinity })
  const userInitials = userProfile?.userInitials
  const [userMenuOpen, setUserMenuOpen] = React.useState(false)

  const isActive = (url: string, title?: string) => {
    if (url === '/') return title === 'Dashboard' && location === '/'
    return location.startsWith(url)
  }

  return (
    <div className='flex h-full w-[92px] shrink-0 flex-col overflow-hidden border-r border-border bg-white text-foreground'>
      {/* Branding */}
      <div className='flex w-full select-none flex-col items-center gap-1 px-2 py-4'>
        <EmergenceIcon className='size-7' aria-label='Emergence AI' />
        <span className='font-sans font-semibold'>Craft</span>
      </div>

      {/* Navigation */}
      <div className='flex flex-col gap-1.5 px-1.5 py-4'>
        {NAV_ITEMS.map((item) => (
          <MenuButton
            key={item.title}
            title={item.title}
            url={item.url}
            Icon={item.Icon}
            isActive={isActive(item.url, item.title)}
            disabled={item.disabled}
            beta={item.beta}
          />
        ))}
      </div>

      {/* User menu */}
      <div className='mb-6 mt-auto p-2'>
        <Popover open={userMenuOpen} onOpenChange={setUserMenuOpen}>
          <PopoverTrigger asChild>
            <Button
              type='button'
              variant='unstyled'
              className='mx-auto flex size-8 items-center justify-center rounded-full border border-neutral-300 bg-orange-50 transition-colors hover:bg-orange-100'
              data-testid='button-profile'
              aria-haspopup='dialog'
              aria-expanded={userMenuOpen}
            >
              <span className='text-xs font-bold text-orange-600'>{userInitials}</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent
            side='right'
            align='start'
            sideOffset={8}
            className='w-[188px] rounded-xl border border-neutral-300 bg-neutral-100 p-1.5 shadow-[0_7px_29px_0_rgba(100,100,111,0.2)]'
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <div className='flex flex-col gap-2'>
              <button
                type='button'
                className='flex h-[29px] w-full items-center gap-2 rounded-lg px-1.5 py-1 text-left text-sm font-normal text-neutral-950 outline-none transition-colors hover:bg-neutral-300 focus-visible:bg-neutral-300'
                data-testid='sidebar-menu-my-profile'
                onClick={() => {
                  setUserMenuOpen(false)
                  navigate({ to: '/profile' })
                }}
              >
                <UserIcon className='size-5 shrink-0' weight='regular' aria-hidden />
                My Profile
              </button>
              <div className='h-px w-full bg-neutral-300' />
              <button
                type='button'
                className='flex h-[29px] w-full items-center gap-2 rounded-lg px-1.5 py-1 text-left text-sm font-normal text-neutral-950 outline-none transition-colors hover:bg-neutral-300 focus-visible:bg-neutral-300'
                data-testid='sidebar-menu-logout'
                onClick={() => {
                  setUserMenuOpen(false)
                  logout()
                }}
              >
                <SignOutIcon className='size-5 shrink-0' weight='regular' aria-hidden />
                Log out
              </button>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  )
}
